import { useState } from "react";
import { X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { CURRENT_LEGAL_VERSION } from "@shared/legal";
import TermsOfUse from "@/pages/TermsOfUse";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";

type LegalDoc = "terms-of-use" | "founding" | "privacy";

// Blocking, non-dismissible consent gate. Shown to a logged-in user whose
// recorded legal-acceptance version is missing or older than the current one.
// New signups are recorded at account creation, so they never see this; it
// catches pre-existing users on first open and everyone after a version bump —
// the way mainstream apps roll out updated terms.
export function LegalUpdateGate() {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDoc, setOpenDoc] = useState<LegalDoc | null>(null);

  const acceptedVersion = (user as any)?.termsAcceptedVersion as string | null | undefined;
  if (!user || acceptedVersion === CURRENT_LEGAL_VERSION) return null;

  const isReturning = !!acceptedVersion;
  const canSell = !!(user as any)?.canSell;

  const handleAgree = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiRequest("POST", "/api/legal/accept", {});
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    } catch (e: any) {
      setError(e?.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/api/logout", { method: "POST", credentials: "include" });
    } catch {
      /* ignore */
    }
    queryClient.setQueryData(["/api/auth/user"], null);
    window.location.assign("/login");
  };

  return (
    <>
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-2xl bg-background p-6 shadow-xl">
        <h2 className="text-xl font-bold text-foreground">
          {isReturning ? "We've updated our terms" : "One quick thing"}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {isReturning
            ? "We've made updates to the terms that govern Stoop. Please review and agree to continue."
            : "Please review and agree to the terms that govern Stoop to continue."}
        </p>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <button
            type="button"
            onClick={() => setOpenDoc("terms-of-use")}
            className="font-medium text-primary underline"
          >
            Terms of Use
          </button>
          {canSell && (
            <button
              type="button"
              onClick={() => setOpenDoc("founding")}
              className="font-medium text-primary underline"
            >
              Founding Maker Terms
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpenDoc("privacy")}
            className="font-medium text-primary underline"
          >
            Privacy Policy
          </button>
        </div>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <div className="mt-5 flex flex-col gap-2">
          <Button onClick={handleAgree} disabled={submitting}>
            {submitting ? "Saving…" : "Agree & Continue"}
          </Button>
          <button
            type="button"
            onClick={handleLogout}
            className="text-xs text-muted-foreground underline"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
    {openDoc && (
      <div className="fixed inset-0 z-[110] flex flex-col bg-background">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-4 py-3">
          <span className="text-sm font-semibold text-foreground">
            {openDoc === "terms-of-use"
              ? "Terms of Use"
              : openDoc === "founding"
                ? "Founding Maker Terms"
                : "Privacy Policy"}
          </span>
          <button
            type="button"
            onClick={() => setOpenDoc(null)}
            className="flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-primary"
          >
            <X className="h-4 w-4" /> Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {openDoc === "terms-of-use" && <TermsOfUse />}
          {openDoc === "founding" && <Terms />}
          {openDoc === "privacy" && <Privacy />}
        </div>
      </div>
    )}
    </>
  );
}
